import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Calendar, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { costTrackingService } from '@/services/costTrackingService';

// AI cost tracking is a platform-level concern shown only under the Joules
// tenant for now — other tenants do not see cost data.
const JOULES_TENANT_ID = '511f29e8-bef3-4065-9927-777d6eafe7f1';

export const CostDashboard: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const isJoulesTenant = currentTenant?.tenant_id === JOULES_TENANT_ID;
  const [loading, setLoading] = useState(true);
  const [monthlyUsage, setMonthlyUsage] = useState<any[]>([]);
  const [totalCost, setTotalCost] = useState(0);
  const [budgetAlert, setBudgetAlert] = useState<any>(null);
  const [tierUsage, setTierUsage] = useState<{ tier: string; cost: number; tokens: number; turns: number }[]>([]);

  // Cost is tracked against the Joules (platform) tenant only.
  const SHARED_TENANT_ID = JOULES_TENANT_ID;

  useEffect(() => {
    if (user?.id && isJoulesTenant) {
      loadCostData();
    } else {
      setLoading(false);
    }
  }, [user, isJoulesTenant]);

  const loadCostData = async () => {
    if (!user?.id || !isJoulesTenant) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Get current month usage - use shared tenant ID
      const usage = await costTrackingService.getMonthlyUsage(SHARED_TENANT_ID);
      setMonthlyUsage(usage || []);
      
      // Calculate total cost
      const total = (usage || []).reduce((sum: number, item: any) => 
        sum + (parseFloat(item.total_cost.toString()) || 0), 0
      );
      setTotalCost(total);

      // Check budget status - use shared tenant ID
      const budget = await costTrackingService.checkBudgetStatus(SHARED_TENANT_ID);
      setBudgetAlert(budget);

      // AIC-3.1: assistant usage by routing tier (haiku vs sonnet)
      const tiers = await costTrackingService.getUsageByTier(SHARED_TENANT_ID);
      setTierUsage(tiers);

    } catch (error) {
      console.error('Error loading cost data:', error);
      // Set default empty state on error
      setMonthlyUsage([]);
      setTotalCost(0);
      setBudgetAlert(null);
      setTierUsage([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    if (!SHARED_TENANT_ID) return;

    try {
      const csvData = await costTrackingService.exportToCSV(SHARED_TENANT_ID);
      
      // Download CSV
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-costs-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  // Cost is shown only under the Joules (platform) tenant for now.
  if (!isJoulesTenant) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <DollarSign className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
          <h3 className="font-medium mb-1">AI cost tracking is managed at the platform level</h3>
          <p className="text-sm text-muted-foreground">
            Assistant usage and spend are tracked centrally under Joules. Switch to the
            Joules tenant to view cost data.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cost Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalCost.toFixed(4)}</div>
            <p className="text-xs text-muted-foreground">
              Current month usage
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Operations</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {monthlyUsage.reduce((sum, item) => sum + (parseInt(item.total_tokens.toString()) || 0), 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              Total tokens processed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Budget Status</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {budgetAlert ? (
              <>
                <div className="text-2xl font-bold">
                  {budgetAlert.percentage_used.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground">
                  of ${budgetAlert.threshold} budget used
                </p>
                {budgetAlert.alert_triggered && (
                  <Badge variant="destructive" className="mt-1">
                    Budget Alert
                  </Badge>
                )}
              </>
            ) : (
              <>
                <div className="text-2xl font-bold">-</div>
                <p className="text-xs text-muted-foreground">
                  No budget set
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* AIC-3.1: Assistant usage by routing tier */}
      {tierUsage.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Assistant Cost by Model Tier</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {tierUsage.map((t) => (
                <div key={t.tier} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium capitalize">{t.tier}</span>
                    <Badge variant="secondary" className="text-xs">{t.turns} turn{t.turns !== 1 ? 's' : ''}</Badge>
                  </div>
                  <div className="text-xl font-bold">${t.cost.toFixed(4)}</div>
                  <p className="text-xs text-muted-foreground">{t.tokens.toLocaleString()} tokens this month</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Operation Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Cost Breakdown by Operation</span>
            <Button onClick={handleExportData} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {monthlyUsage.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No usage data yet</h3>
              <p className="text-muted-foreground">
                Start using the knowledge base to see cost tracking
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {monthlyUsage.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium capitalize">{item.operation_type}</h4>
                    <p className="text-sm text-muted-foreground">
                      {parseInt(item.total_tokens.toString()).toLocaleString()} tokens
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">${parseFloat(item.total_cost.toString()).toFixed(4)}</div>
                    <Badge variant="outline">
                      {item.operation_type === 'embedding' ? 'Text Processing' : 
                       item.operation_type === 'query' ? 'Search Queries' : 'AI Responses'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cost Information */}
      <Card>
        <CardHeader>
          <CardTitle>Understanding AI Costs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-3 border rounded-lg">
              <h4 className="font-medium mb-2">Embeddings</h4>
              <p className="text-sm text-muted-foreground">
                Generated when documents are uploaded and processed into vector representations.
              </p>
              <Badge variant="outline" className="mt-2">~$0.0001 per 1K tokens</Badge>
            </div>

            <div className="p-3 border rounded-lg">
              <h4 className="font-medium mb-2">Queries</h4>
              <p className="text-sm text-muted-foreground">
                Vector similarity searches when AI retrieves relevant knowledge.
              </p>
              <Badge variant="outline" className="mt-2">Minimal cost</Badge>
            </div>

            <div className="p-3 border rounded-lg">
              <h4 className="font-medium mb-2">Completions</h4>
              <p className="text-sm text-muted-foreground">
                AI responses generated using GPT models with knowledge context.
              </p>
              <Badge variant="outline" className="mt-2">~$0.002 per 1K tokens</Badge>
            </div>

            <div className="p-3 border rounded-lg">
              <h4 className="font-medium mb-2">Optimization Tips</h4>
              <p className="text-sm text-muted-foreground">
                Costs are typically very low. Large documents generate more embeddings.
              </p>
              <Badge variant="secondary" className="mt-2">Monitor monthly usage</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};